import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamEventId, ParamIssueShortId, ParamIssueUrl, ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { logError } from "./logging-B7X3yL0T.js";
import { ApiNotFoundError } from "./errors-DG2FreDU.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { addAIConversationSuggestedActions } from "./ai-conversation-actions-rbKawWyG.js";
import { enhanceNotFoundError } from "./enhance-error-BDXGhe5E.js";
import { parseIssueParams } from "./issue-helpers-0Dnme3Ym.js";
import { formatIssueOutput } from "./formatting-Cviy8-dX.js";
import { assertIssueWithinProjectConstraint } from "./issue-D65xI0F7.js";
import { resolveCodeLocation } from "./code-location-C0fIXD0V.js";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-issue-details.ts
const MAX_AI_CONVERSATION_MATCHES = 3;
const AI_CONVERSATION_LOOKUP_WINDOW_MS = 24 * 60 * 60 * 1e3;
const TRACE_ID_PATTERN = /^[0-9a-fA-F]{32}$/;
var get_issue_details_default = defineTool({
	name: "get_issue_details",
	skills: [
		"inspect",
		"triage",
		"seer"
	],
	requiredScopes: ["event:read"],
	description: [
		"Get detailed information about a specific Sentry issue by ID.",
		"",
		"USE THIS TOOL WHEN USERS:",
		"- Provide a specific issue ID (e.g., 'CLOUDFLARE-MCP-41', 'PROJECT-123')",
		"- Ask to 'explain [ISSUE-ID]', 'tell me about [ISSUE-ID]'",
		"- Want details/stacktrace/analysis for a known issue",
		"- Provide a Sentry issue URL",
		"",
		"DO NOT USE for:",
		"- General searching or listing issues (use search_issues)",
		"",
		"TRIGGER PATTERNS:",
		"- 'Explain ISSUE-123' → use get_issue_details",
		"- 'Tell me about PROJECT-456' → use get_issue_details",
		"- 'What happened in [issue URL]' → use get_issue_details",
		"",
		"<examples>",
		"### With Sentry URL (recommended - simplest approach)",
		"```",
		"get_issue_details(issueUrl='https://sentry.sentry.io/issues/6916805731/?project=4509062593708032&query=is%3Aunresolved')",
		"```",
		"",
		"### With issue ID and organization",
		"```",
		"get_issue_details(organizationSlug='my-organization', issueId='CLOUDFLARE-MCP-41')",
		"```",
		"",
		"### With event ID and organization",
		"```",
		"get_issue_details(organizationSlug='my-organization', eventId='c49541c747cb4d8aa3efb70ca5aba243')",
		"```",
		"</examples>",
		"",
		"<hints>",
		"- **IMPORTANT**: If user provides a Sentry URL, pass the ENTIRE URL to issueUrl parameter unchanged",
		"- When using issueUrl, all other parameters are automatically extracted - don't provide them separately",
		"- If using issueId (not URL), then organizationSlug is required",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug.optional(),
		regionUrl: ParamRegionUrl.nullable().default(null),
		issueId: ParamIssueShortId.optional(),
		eventId: ParamEventId.optional(),
		issueUrl: ParamIssueUrl.optional()
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		if (params.eventId) {
			const orgSlug$1 = params.organizationSlug;
			const eventId = params.eventId;
			if (!orgSlug$1) throw new UserInputError("`organizationSlug` is required when providing `eventId`");
			setTag("organization.slug", orgSlug$1);
			let issue$1;
			if (params.issueId) issue$1 = await apiService.getIssue({
				organizationSlug: orgSlug$1,
				issueId: params.issueId
			});
			else {
				const [found] = await apiService.listIssues({
					organizationSlug: orgSlug$1,
					query: eventId
				});
				if (!found) return `# Event Not Found\n\nNo issue found for Event ID: ${eventId}`;
				issue$1 = found;
			}
			assertIssueWithinProjectConstraint({
				issue: issue$1,
				projectSlug: context.constraints.projectSlug
			});
			const [{ event: event$1, performanceTrace: performanceTrace$1, aiConversations: aiConversations$1, codeLocation: codeLocation$1 }, { autofixState: autofixState$1, externalIssues: externalIssues$1, relatedReplayIds: relatedReplayIds$1 }] = await Promise.all([apiService.getEventForIssue({
				organizationSlug: orgSlug$1,
				issueId: issue$1.shortId,
				eventId
			}).catch((error) => {
				if (error instanceof ApiNotFoundError) throw enhanceNotFoundError(error, {
					organizationSlug: orgSlug$1,
					issueId: issue$1.shortId,
					eventId
				});
				throw error;
			}).then(async (event$2) => ({
				event: event$2,
				...await fetchEventEnrichment({
					apiService,
					organizationSlug: orgSlug$1,
					event: event$2,
					issue: issue$1
				})
			})), fetchIssueEnrichmentData({
				apiService,
				organizationSlug: orgSlug$1,
				issue: issue$1,
				seerEnabled: isSeerGranted(context)
			})]);
			return addAIConversationSuggestedActions({
				markdown: formatIssueOutput({
					organizationSlug: orgSlug$1,
					issue: issue$1,
					event: event$1,
					apiService,
					autofixState: autofixState$1,
					performanceTrace: performanceTrace$1,
					externalIssues: externalIssues$1,
					relatedReplayIds: relatedReplayIds$1,
					aiConversations: aiConversations$1,
					codeLocation: codeLocation$1,
					experimentalMode: context.experimentalMode,
					availableToolNames: context.availableToolNames,
					directToolNames: context.directToolNames
				}),
				organizationSlug: orgSlug$1,
				aiConversations: aiConversations$1,
				experimentalMode: context.experimentalMode ?? false,
				availableToolNames: context.availableToolNames,
				directToolNames: context.directToolNames
			});
		}
		if (!params.issueUrl && !params.issueId) throw new UserInputError("Either `issueId` or `issueUrl` must be provided");
		if (!params.issueUrl && !params.organizationSlug) throw new UserInputError("`organizationSlug` is required when providing `issueId`");
		const { organizationSlug: orgSlug, issueId: parsedIssueId } = parseIssueParams({
			organizationSlug: params.organizationSlug,
			issueId: params.issueId,
			issueUrl: params.issueUrl
		});
		setTag("organization.slug", orgSlug);
		let issue;
		try {
			issue = await apiService.getIssue({
				organizationSlug: orgSlug,
				issueId: parsedIssueId
			});
		} catch (error) {
			if (error instanceof ApiNotFoundError) throw enhanceNotFoundError(error, {
				organizationSlug: orgSlug,
				issueId: parsedIssueId
			});
			throw error;
		}
		assertIssueWithinProjectConstraint({
			issue,
			projectSlug: context.constraints.projectSlug
		});
		const [{ event, performanceTrace, aiConversations, codeLocation }, { autofixState, externalIssues, relatedReplayIds }] = await Promise.all([apiService.getLatestEventForIssue({
			organizationSlug: orgSlug,
			issueId: issue.shortId
		}).then(async (event$1) => ({
			event: event$1,
			...await fetchEventEnrichment({
				apiService,
				organizationSlug: orgSlug,
				event: event$1,
				issue
			})
		})), fetchIssueEnrichmentData({
			apiService,
			organizationSlug: orgSlug,
			issue,
			seerEnabled: isSeerGranted(context)
		})]);
		return addAIConversationSuggestedActions({
			markdown: formatIssueOutput({
				organizationSlug: orgSlug,
				issue,
				event,
				apiService,
				autofixState,
				performanceTrace,
				externalIssues,
				relatedReplayIds,
				aiConversations,
				codeLocation,
				experimentalMode: context.experimentalMode,
				availableToolNames: context.availableToolNames,
				directToolNames: context.directToolNames
			}),
			organizationSlug: orgSlug,
			aiConversations,
			experimentalMode: context.experimentalMode ?? false,
			availableToolNames: context.availableToolNames,
			directToolNames: context.directToolNames
		});
	}
});
async function fetchEventEnrichment({ apiService, organizationSlug, event, issue }) {
	const [performanceTrace, aiConversations, codeLocation] = await Promise.all([
		maybeFetchPerformanceTrace({
			apiService,
			organizationSlug,
			event
		}),
		maybeFindAIConversationsForIssueEvent({
			apiService,
			organizationSlug,
			event
		}),
		resolveCodeLocation({
			apiService,
			organizationSlug,
			projectSlug: issue.project.slug,
			event
		})
	]);
	return {
		performanceTrace,
		aiConversations,
		codeLocation
	};
}
/**
* Fetches supplementary data for an issue in parallel: Seer analysis and external links.
* All calls are non-blocking -- failures are silently caught so they never
* prevent the primary issue details from being returned. Seer analysis is
* skipped entirely when the `seer` skill is not granted.
*/
async function fetchIssueEnrichmentData({ apiService, organizationSlug, issue, seerEnabled }) {
	const issueId = String(issue.id);
	const [autofixState, externalIssues, relatedReplayIds] = await Promise.all([
		maybeFetchAutofixState({
			apiService,
			organizationSlug,
			issue,
			seerEnabled
		}),
		apiService.getIssueExternalLinks({
			organizationSlug,
			issueId: issue.shortId
		}).catch(() => void 0),
		apiService.listReplayIdsForIssue({
			organizationSlug,
			issueId,
			dataSource: getReplayDataSource(issue)
		}).catch(() => void 0)
	]);
	return {
		autofixState,
		externalIssues,
		relatedReplayIds
	};
}
/**
* Whether Seer output can be used at all in this session.
*
* A session started with `--disable-skills=seer` (or `--skills` that omits it)
* drops Seer output on the floor, so requesting it is pure overhead -- and on
* deployments without Seer the autofix endpoint answers 500, making it a
* guaranteed-failing request on every issue lookup. An unknown granted set
* keeps the previous behaviour and fetches.
*/
function isSeerGranted(context) {
	return context.grantedSkills ? context.grantedSkills.has("seer") : true;
}
async function maybeFetchAutofixState({ apiService, organizationSlug, issue, seerEnabled }) {
	if (!seerEnabled) return void 0;
	return apiService.getAutofixState({
		organizationSlug,
		issueId: issue.shortId
	}).catch(() => void 0);
}
async function maybeFetchPerformanceTrace({ apiService, organizationSlug, event }) {
	const context = shouldFetchTraceForEvent(event);
	if (!context) return void 0;
	try {
		return await apiService.getTrace({
			organizationSlug,
			traceId: context.traceId,
			limit: 1e4
		});
	} catch (error) {
		logError(error);
		return void 0;
	}
}
async function maybeFindAIConversationsForIssueEvent({ apiService, organizationSlug, event }) {
	const traceId = getEventTraceId(event);
	if (!traceId || !TRACE_ID_PATTERN.test(traceId)) return [];
	try {
		return await findAIConversationsForTrace({
			apiService,
			organizationSlug,
			traceId,
			event
		});
	} catch (error) {
		logError(error);
		return [];
	}
}
/**
* Conversation IDs live on spans, not issue events. Keep this as a bounded
* opportunistic span match so issue details never fetch the full trace.
*/
async function findAIConversationsForTrace({ apiService, organizationSlug, traceId, event }) {
	const response = await apiService.searchEvents({
		organizationSlug,
		dataset: "spans",
		query: `trace:${traceId} has:gen_ai.conversation.id`,
		fields: [
			"gen_ai.conversation.id",
			"span_id",
			"timestamp"
		],
		limit: MAX_AI_CONVERSATION_MATCHES,
		sort: "-timestamp",
		...buildConversationLookupTimeParams(event)
	});
	if (!response || typeof response !== "object" || !Array.isArray(response.data)) return [];
	const references = /* @__PURE__ */ new Map();
	for (const row of response.data) {
		if (!row || typeof row !== "object") continue;
		const record = row;
		const conversationId = getString(record["gen_ai.conversation.id"]);
		if (!conversationId || references.has(conversationId)) continue;
		references.set(conversationId, {
			conversationId,
			spanId: getString(record.span_id)
		});
		if (references.size >= MAX_AI_CONVERSATION_MATCHES) break;
	}
	return [...references.values()];
}
function buildConversationLookupTimeParams(event) {
	const eventTimestamp = getEventTimestamp(event);
	if (!eventTimestamp) return { statsPeriod: "14d" };
	return {
		start: new Date(eventTimestamp.getTime() - AI_CONVERSATION_LOOKUP_WINDOW_MS).toISOString(),
		end: new Date(eventTimestamp.getTime() + AI_CONVERSATION_LOOKUP_WINDOW_MS).toISOString()
	};
}
function getEventTimestamp(event) {
	const timestamp = getString(event.dateCreated) ?? getString(event.dateReceived);
	if (!timestamp) return null;
	const date = new Date(timestamp);
	return Number.isNaN(date.getTime()) ? null : date;
}
function getEventTraceId(event) {
	const traceId = event.contexts?.trace?.trace_id;
	return getString(traceId);
}
function getString(value) {
	return typeof value === "string" && value.length > 0 ? value : void 0;
}
function isErrorEvent(event) {
	return event.type === "error" || event.type === "default";
}
function shouldFetchTraceForEvent(event) {
	if (isErrorEvent(event)) return null;
	const traceId = event.contexts?.trace?.trace_id;
	if (typeof traceId !== "string" || traceId.length === 0) return null;
	return { traceId };
}
function getReplayDataSource(issue) {
	return issue.issueCategory === "error" || issue.type === "error" ? "discover" : "search_issues";
}

//#endregion
export { get_issue_details_default };
//# sourceMappingURL=get-issue-details-DHDUffp5.js.map